import React, {useRef, useEffect, useState, useCallback, useMemo} from 'react';
import {Props} from './ac-accordion.proptypes';
import clsx from 'clsx';

// Imports => Utilities
import {AcIsSet, AcUUID} from '@asdf-utils';

// Imports => Atoms
import {AcCard} from '@asdf-atoms/ac-card';

const _CLASSES = {
  MAIN: 'ac-accordion',
  EXPANDED: 'ac-accordion--expanded',
  LIMITED: 'ac-accordion--limited',
  TITLE: 'ac-accordion__title',
  THEMES: {
    DEFAULT: 'ac-accordion--default',
    ALPHA: 'ac-accordion--alpha',
    ALPHA_LIGHT: 'ac-accordion--alpha-light',
    OMEGA: 'ac-accordion--omega',
    OMEGA_LIGHT: 'ac-accordion--omega-light',
    WHITE: 'ac-accordion--white',
    LIGHT: 'ac-accordion--light',
    SUBTLE: 'ac-accordion--subtle',
    MEDIUM: 'ac-accordion--medium',
    PITCH: 'ac-accordion--pitch',
    TRANSPARENT: 'ac-accordion--transparent',
  },
  CONTENT: {
    MAIN: 'ac-accordion__content',
    CHILDREN: 'ac-accordion__content__children',
  },
};

const AcAccordion = ({
  id = AcUUID(),
  title,
  theme = 'default',
  children,
  open = false,
  limited = false,
  disabled = false,
  callback,
  className,
  titleClassName,
  childrenClassName,
}) => {
  const [expanded, setExpanded] = useState(open);
  const [height, setHeight] = useState(0);

  const $content = useRef(null);

  useEffect(() => {
    setExpanded(open);
  }, [open]);

  useEffect(() => {
    calculateContentHeight();
  }, [expanded]);

  const handleToggle = event => {
    const key = event.key || event.which;

    if (event.type !== 'click' && key && key !== 1 && (key !== 13 || key !== 'Enter')) return;

    const state = expanded;
    setExpanded(!state);
    calculateContentHeight();
    if (callback) callback(!state, id);
  };

  const calculateContentHeight = useCallback(() => {
    if (!AcIsSet($content) || !AcIsSet($content.current)) return;

    let height = 20;

    if ($content.current && $content.current.childNodes) {
      const children = $content.current.childNodes;
      const collection = children;
      const len = collection.length;
      let n = 0;

      for (n; n < len; n++) {
        const node = collection[n];
        height += node.scrollHeight;
      }
    }

    setHeight(height);
  }, [$content, limited]);

  const getContentClassNames = useMemo(() => {
    return clsx(_CLASSES.CONTENT.MAIN);
  }, []);

  const getChildrenClassNames = useMemo(() => {
    return clsx(_CLASSES.CONTENT.CHILDREN, childrenClassName);
  }, []);

  const getTitleClassNames = useMemo(() => {
    return clsx(_CLASSES.TITLE, titleClassName);
  }, []);

  const getCardClassNames = useMemo(() => {
    return clsx(
      _CLASSES.MAIN,
      expanded && _CLASSES.EXPANDED,
      limited && _CLASSES.LIMITED,
      theme && _CLASSES.THEMES[theme.toUpperCase()],
      className
    );
  }, [expanded, limited, theme, className]);

  const getBodyInlineStyles = useMemo(() => {
    return {
      height: expanded ? `${height / 10 || 0}rem` : '0rem',
    };
  }, [expanded, height]);

  return (
    <AcCard className={getCardClassNames} theme={theme} aria-expanded={expanded}>
      <h3 className={getTitleClassNames} id={`ac-accordion-${id}-title`}>
        <button
          type={'button'}
          disabled={disabled}
          onClick={handleToggle}
          onKeyUp={handleToggle}
          aria-expanded={expanded}
          aria-controls={`ac-accordion-${id}-region`}
          aria-disabled={disabled}
        >
          {title}
        </button>
      </h3>
      <div
        ref={$content}
        role={'region'}
        aria-labelledby={`ac-accordion-${id}-title`}
        style={getBodyInlineStyles}
        id={`ac-accordion-${id}-region`}
        className={getContentClassNames}
      >
        <div className={getChildrenClassNames}>{children}</div>
      </div>
    </AcCard>
  );
};

AcAccordion.displayName = 'AcAccordion';
AcAccordion.propTypes = Props;

export {AcAccordion};
export default AcAccordion;
